import type { RouterContextType } from "@/api/permissions/permissions.api";
import ServEngineIcon from "@/assets/servengine-icon.svg";
import ServEngineLogo from "@/assets/servengine.svg";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { useIsMobileNavbar } from "@/hooks/useIsMobileNavbar";
import { ActionIcon, Drawer, Image, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconArrowBackUp, IconForms, IconMenu2, IconUsersGroup, IconX } from "@tabler/icons-react";
import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Breadcrumbs } from "@/components/features/AuthenticatedLayout/Breadcrumbs/Breadcrumbs";
import SidebarItem from "@/components/features/AuthenticatedLayout/Sidebar/SidebarItem";
import { Button } from "@onengine/component-library";
import {
  AssignmentsAssistantButton,
  AssignmentsAssistantLayout,
} from "@/components/features/FormBuilder/Assignments/Assistant/AssignmentsAssistantLayout";
import { isEntityScoped, useEntityTenantStore } from "@/store/entityTenantStore";
import styles from "./form-builder-layout.module.scss";

export const Route = createFileRoute("/form-builder/_formBuilderLayout")({
  component: FormBuilderLayout,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const NAV_ITEMS = [
  { to: "/form-builder/templates", label: "Form Templates", icon: IconForms },
  { to: "/form-builder/assignments", label: "Assignments", icon: IconUsersGroup },
] as const;

type FormBuilderNavProps = {
  pathname: string;
  onNavigate?: () => void;
  onClose?: () => void;
};

function FormBuilderNav({ pathname, onNavigate, onClose }: FormBuilderNavProps) {
  const navigate = useNavigate();

  return (
    <>
      <div className={styles.logoRow}>
        <Image src={ServEngineIcon} w={28} h={28} alt="ServEngine" />
        <span className={styles.logoText}>ServEngine</span>
        {onClose && (
          <ActionIcon
            variant="subtle"
            size={44}
            c="var(--icon-default)"
            onClick={onClose}
            aria-label="Close navigation"
            className={styles.closeButton}
          >
            <IconX size={20} />
          </ActionIcon>
        )}
      </div>

      <nav className={styles.nav} aria-label="Form Builder">
        <Text size="xs" c="dimmed" px="xs" pt="xs">
          Form Builder
        </Text>
        {NAV_ITEMS.map((item) => (
          <SidebarItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={<item.icon size={18} />}
            opened={true}
            active={pathname.startsWith(item.to)}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <div className={styles.returnButton}>
        <Button
          variant="neutral-secondary"
          iconLeft={<IconArrowBackUp size={16} />}
          fullWidth
          onClick={() => navigate({ to: "/" })}
        >
          Return to ServEngine
        </Button>
      </div>

      <div className={styles.sidebarFooter}>
        <Text size="xs" c="dimmed">
          Powered by
        </Text>
        <Image src={ServEngineLogo} w="auto" h={16} alt="ServEngine logo" fit="contain" />
      </div>
    </>
  );
}

export function FormBuilderLayout() {
  const location = useLocation();
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const scopedTenantId = entityTenant && isEntityScoped(entityTenant) ? entityTenant : undefined;

  const isMobile = useIsMobileNavbar();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const drawerOpened = isMobile && mobileOpened;

  useEffect(() => {
    if (!isMobile) closeMobile();
  }, [isMobile, closeMobile]);

  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  const activeItem = NAV_ITEMS.find((item) => location.pathname.startsWith(item.to));

  const breadcrumbItems = activeItem
    ? [
        {
          title: activeItem.label === "Form Templates" ? "Templates" : activeItem.label,
          realHref: activeItem.to,
          maskedHref: activeItem.to,
          icon: <activeItem.icon size={16} />,
        },
        {
          title: location.pathname.includes("/edit") ? "Editor" : "List View",
          realHref: location.pathname,
          maskedHref: location.pathname,
        },
      ]
    : [
        {
          title: "Form Builder",
          realHref: location.pathname,
          maskedHref: location.pathname,
        },
      ];

  const headerBreadcrumbItems = isMobile
    ? [{ ...breadcrumbItems[breadcrumbItems.length - 1], icon: breadcrumbItems[0]?.icon }]
    : breadcrumbItems;

  return (
    <div className={styles.shell}>
      {isMobile ? (
        <Drawer.Root opened={drawerOpened} onClose={closeMobile} size={280} padding={0}>
          <Drawer.Overlay />
          {/* className would also land on Drawer's fixed inner wrapper; its column direction
              turns the panel's 280px flex-basis into a height. classNames targets the panel only. */}
          <Drawer.Content
            aria-label="Form Builder navigation"
            classNames={{ content: styles.drawerContent }}
          >
            <Drawer.Body className={styles.drawerBody}>
              <FormBuilderNav
                pathname={location.pathname}
                onNavigate={closeMobile}
                onClose={closeMobile}
              />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      ) : (
        <aside className={styles.sidebar}>
          <FormBuilderNav pathname={location.pathname} />
        </aside>
      )}

      <div className={styles.main}>
        <header className={styles.topBar}>
          <div className={styles.topBarStart}>
            {isMobile && (
              <ActionIcon
                variant="subtle"
                size={44}
                c="var(--icon-default)"
                onClick={toggleMobile}
                aria-label="Open navigation"
                aria-expanded={drawerOpened}
              >
                <IconMenu2 size={20} />
              </ActionIcon>
            )}
            <Breadcrumbs items={headerBreadcrumbItems} showLinkButton={false} />
          </div>
          <AssignmentsAssistantButton />
        </header>

        <main className={styles.content}>
          {/* Assignments pages place the assistant themselves, with the company and
              slot on screen; every other page gets it for the company picked. */}
          {location.pathname.startsWith("/form-builder/assignments") ? (
            <Outlet />
          ) : (
            <AssignmentsAssistantLayout tenantId={scopedTenantId}>
              <Outlet />
            </AssignmentsAssistantLayout>
          )}
        </main>
      </div>
    </div>
  );
}
